// editor-modes3.tsx - studio modes: Environment, Characters
// Ported from the design source (editor-modes3.jsx). Environment choices
// persist into the project config and derive the runtime scene theme, so
// Edit and Preview render the chosen sky, sun and fog.
// Exports: EnvironmentPresetsPanel, EnvironmentPreviewPanel,
//          EnvironmentPropertiesPanel, CharactersRoute
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { useEffect, useRef, useState } from 'react';
import { toast } from '../design/toast';
import { getCharacterRenderer, NEUTRAL_BIPED_DEFINITION } from '../../engine';
import type { CharacterDefinition, SceneTheme } from '../../engine';
import type { MapCharacterInstance, MapFloorLevel, PatrolLoopMode, WaypointPatrol } from '../../engine';
import { Link } from '../design/router';
import { saveCharacterRoster, saveMapDocument } from '../persistence/projectRepository';
import { createDebouncedSaver } from './debouncedSave';
import { useEditorHost } from './EditorHost';
import { useProject } from '../project/ProjectProvider';
import { useSharedEnvironmentSlice } from './panel-system/sharedEnvironmentSlice';
import { useCharactersSelectedId } from './panel-system/selectionStores';
import { M2Label, M2Panel } from './editor-modes2';
import { EdSlider } from './panelPrimitives';

/* ── Environment ──────────────────────────────────────────────────────── */
export interface EnvPreset {
  name: string;
  sky: [string, string, string];
  sun: string;
  /**
   * Starting Time of day / Sun intensity / Fog density this preset dials in
   * when picked, so the scene it produces actually matches its own colours
   * (a night preset shouldn't leave a scene at full daylight intensity).
   * The sliders on the right still override these per scene afterwards.
   */
  time: number;
  sunIntensity: number;
  fog: number;
}

export const ENV_PRESETS: EnvPreset[] = [
  { name: 'Midday clear', sky: ['#274a8c', '#5b86c8', '#9cc0e8'], sun: '#fdf3d8', time: 12, sunIntensity: 100, fog: 10 },
  { name: 'Golden hour', sky: ['#2b2040', '#8c4a3a', '#e8975c'], sun: '#f4d27c', time: 17, sunIntensity: 70, fog: 28 },
  { name: 'Overcast', sky: ['#2a2e36', '#454b56', '#6e7480'], sun: '#c9ced8', time: 13, sunIntensity: 50, fog: 55 },
  { name: 'Night ops', sky: ['#04050c', '#0a0f24', '#16204a'], sun: '#bbe5fe', time: 22, sunIntensity: 30, fog: 20 },
  { name: 'Dusk storm', sky: ['#14101f', '#33284a', '#5a4a78'], sun: '#988af6', time: 19, sunIntensity: 40, fog: 70 },
];

/** "17:00" / "17:30" - time is hours with an optional half-hour fraction. */
function formatTimeOfDay(v: number): string {
  return String(Math.floor(v)).padStart(2, '0') + ':' + (v % 1 === 0 ? '00' : '30');
}

/** Derive the runtime scene theme from the environment-mode choices. */
export function themeFromEnvironment(
  presetName: string,
  time: number,
  fog: number,
  base: SceneTheme,
  extras?: { sun?: number; bloom?: number; vignette?: number },
): SceneTheme {
  const p = ENV_PRESETS.find((x) => x.name === presetName) ?? ENV_PRESETS[0]!;
  const night = time < 6 || time > 20;
  const daylight = Math.max(0, Math.sin(((time - 5) / 15) * Math.PI));
  // Sun intensity is a percentage multiplier; 70 is the neutral default.
  const sunScale = (extras?.sun ?? 70) / 70;
  const bloom = extras?.bloom ?? 0;
  const vignette = extras?.vignette ?? 0;
  return {
    ...base,
    background: p.sky[1],
    fog: fog > 0 ? { color: p.sky[2], near: 12 - (fog / 100) * 8, far: 80 - (fog / 100) * 55 } : undefined,
    ambient: { ...base.ambient, intensity: night ? 0.35 : 0.5 + daylight * 0.5 },
    hemisphere: { sky: p.sky[0], ground: '#202833', intensity: night ? 0.5 : 1.1 },
    directional: {
      ...(base.directional ?? { intensity: 1.1, position: [6, 12, 8] as [number, number, number] }),
      color: p.sun,
      intensity: (night ? 0.4 : 0.9 + daylight * 1.4) * sunScale,
    },
    postEffects: bloom > 0 || vignette > 0,
    bloom: bloom > 0 ? bloom / 100 : undefined,
    vignette: vignette > 0 ? vignette / 100 : undefined,
  };
}

/** Left column of the Scene workspace's Environment tab (docs/editor-
 * consolidation/panel-content-spec.md §11): `ENV_PRESETS` rows. A preset
 * click sets preset+time+sun+fog together in one shared-slice update,
 * preserving the original single-render atomicity so the preview beside it
 * never sees an intermediate, incoherent combination of the four values.
 * Composed into one panel by scenePanels.tsx rather than registered as its
 * own docked panel - see that file for why. */
export function EnvironmentPresetsPanel() {
  const { values, update } = useSharedEnvironmentSlice();

  return (
    <M2Panel side="left">
      <M2Label>Presets</M2Label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '0 12px' }}>
        {ENV_PRESETS.map((x) => (
          <button key={x.name} onClick={() => update({ preset: x.name, time: x.time, sun: x.sunIntensity, fog: x.fog })} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 9, textAlign: 'left',
            border: '1px solid ' + (values.preset === x.name ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
            background: values.preset === x.name ? 'var(--ss-accent-soft)' : 'transparent',
            color: values.preset === x.name ? 'var(--ss-accent)' : 'var(--ss-text-2)', fontSize: 'var(--ss-fs-sm)', fontWeight: 500,
          }}>
            <span style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, background: 'linear-gradient(180deg, ' + x.sky[0] + ', ' + x.sky[2] + ')', border: '1px solid rgba(255,255,255,0.1)' }} />
            {x.name}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 'auto', padding: '18px 16px 4px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
        Presets are starting points. Everything on the right overrides them per scene.
      </div>
    </M2Panel>
  );
}

/** Centre column of the Environment tab (docs/editor-consolidation/
 * panel-content-spec.md §11): the 2D flat-colour or 3D sky/sun/horizon
 * mockup - a pure, read-only consumer of the shared slice. */
export function EnvironmentPreviewPanel({ projection }: { projection?: '2D' | '3D' }) {
  const { values } = useSharedEnvironmentSlice();
  const { preset, time, fog } = values;
  const is2d = projection === '2D';
  const p = ENV_PRESETS.find((x) => x.name === preset) ?? ENV_PRESETS[0]!;
  // sun arc position from time of day
  const t = Math.max(0, Math.min(1, (time - 5) / 15));
  const sunX = 8 + t * 84;
  const sunY = 62 - Math.sin(t * Math.PI) * 46;
  const night = time < 6 || time > 20;

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(ellipse 70% 60% at 50% 40%, var(--ss-surface), var(--ss-bg-2) 80%)', padding: 28 }}>
      {is2d ? (
        // A top-down 2D scene never renders a sky, sun or horizon - the
        // runtime canvas is just this background colour behind the
        // tilemap - so the preview shows that colour plainly instead of
        // the side-view horizon below, which would show a scene this
        // project can never actually look like.
        <div style={{
          position: 'relative', width: '100%', maxWidth: 960, aspectRatio: '16 / 9', borderRadius: 12, overflow: 'hidden',
          border: '1px solid var(--ss-border)', boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
          background: p.sky[1],
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }} />
          <span style={{
            position: 'absolute', top: 12, left: 12, background: 'var(--ss-glass)', backdropFilter: 'blur(8px)',
            border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '6px 13px',
            fontWeight: 500, fontSize: 10.5, letterSpacing: '0.05em', color: 'var(--ss-text)',
          }}>{preset} · top-down</span>
          <span style={{
            position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', textAlign: 'center', maxWidth: '80%',
            background: 'var(--ss-glass)', backdropFilter: 'blur(8px)',
            border: '1px solid var(--ss-border-soft)', borderRadius: 10, padding: '7px 14px',
            fontSize: 11, color: 'var(--ss-text-2)', lineHeight: 1.5,
          }}>Top-down 2D scenes only ever show this background colour behind the tilemap. Sky, sun and fog below have no effect here.</span>
        </div>
      ) : (
        <div style={{
          position: 'relative', width: '100%', maxWidth: 960, aspectRatio: '16 / 9', borderRadius: 12, overflow: 'hidden',
          border: '1px solid var(--ss-border)', boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
          background: 'linear-gradient(180deg, ' + p.sky[0] + ' 0%, ' + p.sky[1] + ' 55%, ' + p.sky[2] + ' 100%)',
        }}>
          {/* sun / moon */}
          <span style={{
            position: 'absolute', left: sunX + '%', top: sunY + '%', width: 26, height: 26, borderRadius: '50%',
            background: p.sun, opacity: night ? 0.7 : 1, transform: 'translate(-50%, -50%)',
            boxShadow: '0 0 36px 10px ' + p.sun + '55',
            transition: 'left 200ms ease, top 200ms ease',
          }} />
          {/* horizon ground */}
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '26%', background: 'linear-gradient(180deg, rgba(8,8,10,0.55), rgba(6,6,7,0.92))' }} />
          {/* fog */}
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '60%', background: 'linear-gradient(180deg, transparent, ' + p.sky[2] + ')', opacity: fog / 140 }} />
          {/* silhouettes */}
          <div style={{ position: 'absolute', left: '18%', bottom: '12%', width: 90, height: 64, borderRadius: 4, background: 'rgba(10,10,12,0.85)' }} />
          <div style={{ position: 'absolute', left: '58%', bottom: '14%', width: 54, height: 96, borderRadius: 4, background: 'rgba(10,10,12,0.8)' }} />
          <span style={{
            position: 'absolute', top: 12, left: 12, background: 'var(--ss-glass)', backdropFilter: 'blur(8px)',
            border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '6px 13px',
            fontWeight: 500, fontSize: 10.5, letterSpacing: '0.05em', color: 'var(--ss-text)',
          }}>{preset} · {formatTimeOfDay(time)}</span>
        </div>
      )}
    </div>
  );
}

/** Right column of the Environment tab (docs/editor-consolidation/
 * panel-content-spec.md §11): Sky & time / Atmosphere / Post processing
 * sliders. */
export function EnvironmentPropertiesPanel({ projection }: { projection?: '2D' | '3D' }) {
  const { values, update } = useSharedEnvironmentSlice();
  const { time, sun, fog, bloom, vignette } = values;
  const is2d = projection === '2D';

  return (
    <M2Panel side="right">
      <M2Label>Sky & time</M2Label>
      <EdSlider label="Time of day" value={time} onChange={(v) => update({ time: v })} min={0} max={23.5} step={0.5} format={formatTimeOfDay} showExactValue />
      <EdSlider label="Sun intensity" value={sun} onChange={(v) => update({ sun: v })} min={20} max={140} hint="A multiplier, not 0-100: 70 is the neutral default, 20 is faint, 140 is blazing." showExactValue />
      <M2Label>Atmosphere</M2Label>
      <EdSlider label="Fog density" value={fog} onChange={(v) => update({ fog: v })} showExactValue />
      <div style={{ padding: '0 16px', marginBottom: 14 }}>
        <span className="ss-field-label" style={{ display: 'block', marginBottom: 7 }}>Weather</span>
        <select className="ss-input ed-select" value="Clear" disabled title="Weather effects arrive with the FX runtime" style={{ opacity: 0.45 }} onChange={() => {}}>
          <option>Clear (more soon)</option>
        </select>
      </div>
      <M2Label>Post processing</M2Label>
      <EdSlider label="Bloom" value={bloom} onChange={(v) => update({ bloom: v })} disabled={is2d} showExactValue
        hint={is2d ? 'Works in 3D; the 2D top-down runtime does not render post processing yet' : undefined} />
      <EdSlider label="Vignette" value={vignette} onChange={(v) => update({ vignette: v })} disabled={is2d} showExactValue
        hint={is2d ? 'Works in 3D; the 2D top-down runtime does not render post processing yet' : undefined} />
      <div style={{ padding: '0 16px' }}>
        <span className="ss-field-label" style={{ display: 'block', marginBottom: 7 }}>Colour grade</span>
        <select className="ss-input ed-select" value="Neutral" disabled title="Colour grading arrives with the post-processing pass" style={{ opacity: 0.45 }} onChange={() => {}}>
          <option>Neutral (more soon)</option>
        </select>
      </div>
    </M2Panel>
  );
}

/* ── Characters ───────────────────────────────────────────────────────── */
const ROSTER_COLOURS = ['var(--ss-accent)', 'var(--ss-info)', 'var(--ss-success)', 'var(--ss-warning)'];

const CHARACTER_KIND_LABELS: Record<CharacterDefinition['kind'], string> = {
  player: 'Player',
  antagonist: 'Antagonist',
  npc: 'NPC',
};
const CHARACTER_KINDS = Object.keys(CHARACTER_KIND_LABELS) as CharacterDefinition['kind'][];

/** What the roster's "Model" field should honestly say about a definition:
 * a real imported/retargeted rig, or the shared neutral biped mesh every
 * other character (including a freshly created one) renders with today. */
function characterModelLabel(definition: CharacterDefinition): string {
  return definition.importedGlbUrl ? 'Custom rig (imported)' : 'Neutral biped';
}

/** Everything the Characters mode's "Body" picker needs, for a given roster
 * and the currently-selected character `c` - pulled out of `CharactersRoute`
 * so it's directly unit-testable without mounting the route (same reasoning
 * as `buildAnimationModels`/`filterAttachableAssets` in screens-editor.tsx). */
export interface BodyPickerState {
  /** Every OTHER roster entry with a real imported body - what populates
   * the dropdown's list of assignable bodies. `c` itself is excluded here
   * (a character can't be assigned its own body FROM the list of OTHER
   * assignable bodies) - see `usesOwnBody`/`selectedValue` for what the
   * picker DISPLAYS when `c`'s current body genuinely is its own, which
   * this exclusion alone would otherwise misrepresent as "no body assigned". */
  bodySources: CharacterDefinition[];
  /** The body source among `bodySources` that matches `c`'s own current
   * `importedGlbUrl`, if any - `c`'s body was assigned FROM that entry via
   * this same picker. */
  matchedBodySource: CharacterDefinition | undefined;
  /** True when `c` has a real imported body that isn't explained by any
   * OTHER roster entry - i.e. it's genuinely `c`'s own rig (auto-rigged or
   * bone-mapped directly onto `c`), not one borrowed from an archetype
   * elsewhere in the roster. */
  usesOwnBody: boolean;
  /** What the picker's `<select>` should show as selected: an assignable
   * body source's own id, `'self'` (see `usesOwnBody`), or `'neutral'`. */
  selectedValue: string;
}

export function computeBodyPickerState(c: CharacterDefinition, roster: CharacterDefinition[]): BodyPickerState {
  const bodySources = roster.filter((x) => x.importedGlbUrl && x.id !== c.id);
  const matchedBodySource = c.importedGlbUrl ? bodySources.find((s) => s.importedGlbUrl === c.importedGlbUrl) : undefined;
  const usesOwnBody = Boolean(c.importedGlbUrl) && !matchedBodySource;
  return { bodySources, matchedBodySource, usesOwnBody, selectedValue: matchedBodySource?.id ?? (usesOwnBody ? 'self' : 'neutral') };
}

/**
 * The patch to apply when the Body picker's selection changes to `choice`
 * (an option value from the picker - `'neutral'`, `'self'`, or a body
 * source's own roster id) against a character currently on `currentRigId`.
 * `null` for a no-op: an unrecognised id, or re-selecting `'self'` (already
 * the current state, and there is nothing else in the list it could mean).
 *
 * Clears `boneMapping` whenever the new rig genuinely differs from
 * `currentRigId` - a mapping saved for a DIFFERENT skeleton's own joint
 * names is meaningless once the rig changes, and leaving it in place would
 * silently keep `isRetargeted` (AnimationRoute) evaluating true against a
 * mapping that no longer corresponds to the character's actual rig. Preserves
 * it when the newly-picked source happens to share the SAME rig the
 * character already had a valid mapping for, rather than always clearing
 * unconditionally.
 */
export function bodyPickerPatch(
  choice: string,
  currentRigId: string,
  bodySources: CharacterDefinition[],
): Partial<CharacterDefinition> | null {
  if (choice === 'self') return null;
  if (choice === 'neutral') {
    return {
      rigId: NEUTRAL_BIPED_DEFINITION.rigId,
      rendererId: NEUTRAL_BIPED_DEFINITION.rendererId,
      importedGlbUrl: undefined,
      ...(currentRigId !== NEUTRAL_BIPED_DEFINITION.rigId ? { boneMapping: undefined } : {}),
    };
  }
  const source = bodySources.find((s) => s.id === choice);
  if (!source) return null;
  return {
    rigId: source.rigId,
    rendererId: source.rendererId,
    importedGlbUrl: source.importedGlbUrl,
    ...(source.rigId !== currentRigId ? { boneMapping: undefined } : {}),
  };
}

function uniqueCharacterName(base: string, taken: Iterable<string>): string {
  const names = new Set(taken);
  if (!names.has(base)) return base;
  let n = 2;
  while (names.has(`${base} ${n}`)) n += 1;
  return `${base} ${n}`;
}

function uniqueCharacterId(taken: Iterable<string>): string {
  const ids = new Set(taken);
  let n = 1;
  let id = `char-custom-${n}`;
  while (ids.has(id)) { n += 1; id = `char-custom-${n}`; }
  return id;
}

/** A fresh roster entry: no import, so it shares the neutral biped's rig and
 * renderer outright (same convention the roster hint below explains), and
 * needs no new `rigBones` entry to render or to pose in Animation Studio. */
function newCharacterDefinition(existing: CharacterDefinition[]): CharacterDefinition {
  return {
    id: uniqueCharacterId(existing.map((c) => c.id)),
    name: uniqueCharacterName('New character', existing.map((c) => c.name)),
    kind: 'npc',
    rigId: NEUTRAL_BIPED_DEFINITION.rigId,
    rendererId: NEUTRAL_BIPED_DEFINITION.rendererId,
  };
}

/** Compact single-character identity preview. Resolves the same
 * character-renderer registry Animation Studio and Character Studio use
 * (populated for every roster entry by `registerProjectCharacters` on
 * project load), so this is the real rig at rest, not a mockup - orbit it
 * to check what a roster entry looks like. It no longer doubles as the
 * placement surface: a placed character is selectable/draggable straight in
 * Edit mode's own viewport (see LevelRenderer's `SceneCharacterSelection`),
 * so this stays a small preview rather than a full-pane turntable. */
function CharacterPreviewStage({ rendererId }: { rendererId: string }) {
  const Renderer = getCharacterRenderer(rendererId);
  return (
    <Canvas shadows camera={{ position: [2.4, 1.6, 3.2], fov: 42 }} style={{ position: 'absolute', inset: 0 }}>
      <color attach="background" args={['#11141b']} />
      <hemisphereLight args={['#bcd2ff', '#202833', 1.1]} />
      <directionalLight color="#fff4e6" intensity={2} position={[3, 6, 4]} castShadow />
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <circleGeometry args={[5, 48]} />
        <meshStandardMaterial color="#181b1f" roughness={0.92} />
      </mesh>
      {/* eslint-disable-next-line react-hooks/static-components -- resolved from the character registry, a stable module-level component. */}
      {Renderer ? <Renderer pose={null} /> : null}
      <OrbitControls enablePan={false} minDistance={1.2} maxDistance={8} target={[0, 0.9, 0]} />
    </Canvas>
  );
}

export function CharactersRoute() {
  const { project, editorBase } = useEditorHost();
  const { setProject, reload } = useProject();
  const roster = project.characters;
  // Shared (docs/editor-consolidation/roadmap.md Phase 2): previously a
  // plain local useState, which meant this couldn't survive Characters mode
  // splitting into sibling panels and wasn't readable by the cross-panel
  // Inspector. Same createTransientSelectionStore pattern Logic/HUD/Audio/
  // Data/FX already use (see panel-system/selectionStores.ts). The store's
  // static initial value is '' rather than the old lazy `roster[0]?.id`
  // useState initializer (a store's `initial` can't be a function of live
  // data) - the effect right below already corrects '' to the first roster
  // id the moment it doesn't match anything, exactly as it always has.
  const [sel, setSel] = useCharactersSelectedId.use();
  useEffect(() => {
    if (!roster.some((x) => x.id === sel)) setSel(roster[0]?.id ?? '');
    // setSel is a stable setter (transientSelection.ts wraps it in
    // useCallback with an empty dep array) - listed for exhaustive-deps,
    // not because its identity ever actually changes.
  }, [roster, sel, setSel]);
  const c = roster.find((x) => x.id === sel) ?? roster[0];

  const [name, setName] = useState(c?.name ?? '');
  useEffect(() => setName(c?.name ?? ''), [c?.id, c?.name]);
  const [creating, setCreating] = useState(false);

  // Debounced roster save, flushing on pagehide/visibilitychange/unmount -
  // the same pattern every autosaving mode in this editor uses.
  const saverRef = useRef<ReturnType<typeof createDebouncedSaver<CharacterDefinition[]>> | null>(null);
  useEffect(() => {
    const saver = createDebouncedSaver<CharacterDefinition[]>((characters, { keepalive }) => {
      void saveCharacterRoster(project.meta.id, characters, project.rigBones, { keepalive }).catch((err: unknown) => {
        console.error('[characters] roster save failed:', err);
        toast('Character changes failed to save: ' + (err instanceof Error ? err.message : 'unknown error'), 'error');
      });
    }, 600);
    saverRef.current = saver;
    const onPageHide = () => saver.flush(true);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') saver.flush(true);
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      saver.flush();
      if (saverRef.current === saver) saverRef.current = null;
    };
  }, [project.meta.id, project.rigBones]);

  function patchSelected(patch: Partial<CharacterDefinition>, flush = false) {
    if (!c) return;
    const next = roster.map((x) => (x.id === c.id ? { ...x, ...patch } : x));
    // setProject first for immediate feedback (same convention saveInstances
    // below uses) - without this, `c`/`roster` stay stale until the debounced
    // save round-trips and something reloads the project, so the Team
    // dropdown, the Body picker, and the 3D preview would all silently keep
    // showing the pre-patch value right after a creator changes it.
    setProject({ ...project, characters: next });
    saverRef.current?.schedule(next);
    if (flush) saverRef.current?.flush();
  }

  // Every other roster entry that already carries a real rigged body (from
  // Animation Studio's "Map bones" or "Auto-rig…" flow) doubles as a
  // reusable body/archetype the "Body" picker below can assign to this
  // character too - the project's own roster is the curated archetype
  // library (3.4), so no separate content needs authoring here.
  const bodyPicker = c ? computeBodyPickerState(c, roster) : null;
  const bodySources = bodyPicker?.bodySources ?? [];

  async function addCharacter() {
    if (creating) return;
    setCreating(true);
    const definition = newCharacterDefinition(roster);
    const next = [...roster, definition];
    try {
      await saveCharacterRoster(project.meta.id, next, project.rigBones);
      await reload();
      setSel(definition.id);
    } catch (err) {
      toast('Could not add a character: ' + (err instanceof Error ? err.message : 'unknown error'), 'error');
    } finally {
      setCreating(false);
    }
  }

  // Placements: where a character stands in the level and, optionally, the
  // waypoint route it patrols - saved straight to the map document (like
  // Pixel Studio's own "Save to assets") rather than through the roster's
  // debounced saver above, since this is level data, not character data.
  // `setProject` first for immediate feedback in Map Builder/Preview, then
  // persist; a failed save is surfaced rather than silently lost.
  const placements = (project.map.characterInstances ?? []).filter((i) => i.characterId === c?.id);
  async function saveInstances(next: MapCharacterInstance[]) {
    const nextMap = { ...project.map, characterInstances: next };
    setProject({ ...project, map: nextMap });
    try {
      await saveMapDocument(project.meta.id, nextMap);
    } catch (err) {
      toast('Character placement failed to save: ' + (err instanceof Error ? err.message : 'unknown error'), 'error');
    }
  }
  function patchInstance(id: string, patch: (i: MapCharacterInstance) => MapCharacterInstance) {
    void saveInstances((project.map.characterInstances ?? []).map((i) => (i.id === id ? patch(i) : i)));
  }
  function addPlacement() {
    if (!c) return;
    const id = 'char_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const next: MapCharacterInstance = { id, characterId: c.id, floor: 0, position: [0, 0, 0] };
    void saveInstances([...(project.map.characterInstances ?? []), next]);
  }
  function removePlacement(id: string) {
    void saveInstances((project.map.characterInstances ?? []).filter((i) => i.id !== id));
  }
  function patchPatrol(id: string, patch: (p: WaypointPatrol) => WaypointPatrol) {
    patchInstance(id, (i) => ({ ...i, patrol: patch(i.patrol ?? { points: [], loopMode: 'loop', speed: 3 }) }));
  }
  function addWaypoint(id: string, instance: MapCharacterInstance) {
    const last = instance.patrol?.points.at(-1);
    const [x, y, z] = last ? [last.x, last.y, last.z ?? 0] : instance.position;
    patchPatrol(id, (p) => ({ ...p, points: [...p.points, { x, y, z, dwellMs: 0 }] }));
  }
  function updateWaypoint(id: string, index: number, patch: Partial<{ x: number; y: number; z: number; dwellMs: number }>) {
    patchPatrol(id, (p) => ({ ...p, points: p.points.map((pt, i) => (i === index ? { ...pt, ...patch } : pt)) }));
  }
  function removeWaypoint(id: string, index: number) {
    patchPatrol(id, (p) => ({ ...p, points: p.points.filter((_, i) => i !== index) }));
  }

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <M2Panel side="left" width={240}>
        <M2Label action={<button className="ss-btn ss-btn--ghost" onClick={() => void addCharacter()} disabled={creating} style={{ padding: '3px 10px', fontSize: 11, borderRadius: 7 }}>{creating ? 'Adding…' : '+ New'}</button>}>Roster</M2Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
          {roster.map((x, i) => (
            <button key={x.id} onClick={() => setSel(x.id)} style={{
              display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderRadius: 8, border: 'none', textAlign: 'left',
              background: sel === x.id ? 'var(--ss-accent-soft)' : 'transparent',
              color: sel === x.id ? 'var(--ss-accent)' : 'var(--ss-text-2)', fontSize: 12.5, fontWeight: 500,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: ROSTER_COLOURS[i % ROSTER_COLOURS.length], flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.name}</span>
            </button>
          ))}
        </div>
        <div style={{ padding: '18px 16px 0', fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
          Every character shares the neutral biped rig, so poses and clips work across the roster, until it has its own imported and mapped rig.
        </div>
      </M2Panel>

      {/* Placement now happens directly in Edit mode's shared viewport (drag a
          placed character like any other object - see LevelRenderer's
          SceneCharacterSelection), so this pane is a compact identity
          preview rather than the placement surface it used to be. */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, background: 'radial-gradient(ellipse 70% 60% at 50% 40%, var(--ss-surface), var(--ss-bg-2) 80%)', padding: 28 }}>
        <div style={{ position: 'relative', width: 220, height: 220, borderRadius: 14, overflow: 'hidden', border: '1px solid var(--ss-border-soft)', boxShadow: '0 20px 50px rgba(0,0,0,0.4)' }}>
          {c && <CharacterPreviewStage key={c.id} rendererId={c.rendererId} />}
          <span style={{
            position: 'absolute', top: 10, left: 10, background: 'var(--ss-glass)', backdropFilter: 'blur(8px)',
            border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '5px 11px',
            fontWeight: 500, fontSize: 10, letterSpacing: '0.05em', color: 'var(--ss-text-2)', pointerEvents: 'none',
          }}>{c?.name ?? 'No characters'}</span>
        </div>

        <div style={{ maxWidth: 320, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
            Placements live in Edit mode&rsquo;s viewport now: select and drag a placed character straight in the scene, or use the placements list on the right for exact values and patrols.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <Link to={editorBase + '/edit'} className="ss-btn ss-btn--primary ss-btn--sm">Open Edit viewport</Link>
            {c && (
              <Link to={editorBase + '/animation'} className="ss-btn ss-btn--ghost ss-btn--sm">Pose in Animation Studio</Link>
            )}
          </div>
        </div>
      </div>

      <M2Panel side="right" width={264}>
        <M2Label>Identity</M2Label>
        {c ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 16px', marginBottom: 6, fontSize: 12.5 }}>
            <div className="ss-field">
              <span className="ss-field-label">Name</span>
              <input
                className="ss-input"
                value={name}
                onChange={(e) => { setName(e.target.value); patchSelected({ name: e.target.value }); }}
                onBlur={() => patchSelected({ name }, true)}
                style={{ padding: '7px 10px', fontSize: 12.5 }}
              />
            </div>
            <div className="ss-field">
              <span className="ss-field-label">Team</span>
              <select
                className="ss-input"
                value={c.kind}
                onChange={(e) => patchSelected({ kind: e.target.value as CharacterDefinition['kind'] }, true)}
                style={{ appearance: 'none', padding: '7px 10px', fontSize: 12.5, borderRadius: 9 }}
              >
                {CHARACTER_KINDS.map((k) => <option key={k} value={k}>{CHARACTER_KIND_LABELS[k]}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--ss-text-3)' }}>Model</span>
              <span style={{ color: 'var(--ss-text-2)' }}>{characterModelLabel(c)}</span>
            </div>
            <div className="ss-field">
              <span className="ss-field-label">Body</span>
              <select
                className="ss-input"
                value={bodyPicker!.selectedValue}
                onChange={(e) => {
                  const patch = bodyPickerPatch(e.target.value, c.rigId, bodySources);
                  if (patch) patchSelected(patch, true);
                }}
                style={{ appearance: 'none', padding: '7px 10px', fontSize: 12.5, borderRadius: 9 }}
              >
                <option value="neutral">Neutral biped</option>
                {bodyPicker!.usesOwnBody && <option value="self">{c.name} (this character's own rig)</option>}
                {bodySources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <Link to={editorBase + '/animation'} style={{ fontSize: 10.5, color: 'var(--ss-text-3)', marginTop: 4, display: 'inline-block' }}>
                + Rig a new body in Animation Studio
              </Link>
            </div>
          </div>
        ) : (
          <div style={{ padding: '0 16px', fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
            No characters yet. Add one from the roster on the left.
          </div>
        )}
        {c && (
          <>
            <M2Label action={<button className="ss-btn ss-btn--ghost" onClick={addPlacement} style={{ padding: '3px 10px', fontSize: 11, borderRadius: 7 }}>+ Place</button>}>Placements</M2Label>
            {placements.length === 0 ? (
              <div style={{ padding: '0 16px', fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
                Not placed in the level yet. &ldquo;+ Place&rdquo; adds it at floor 0, (0, 0, 0).
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '0 16px' }}>
                {placements.map((instance) => (
                  <div key={instance.id} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px', border: '1px solid var(--ss-border-soft)', borderRadius: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 10, fontFamily: 'var(--ss-mono)', color: 'var(--ss-text-3)' }}>{instance.id.split('_').slice(-1)[0]}</span>
                      <button onClick={() => removePlacement(instance.id)} title="Remove placement" style={{ border: 'none', background: 'transparent', color: 'var(--ss-text-3)', fontSize: 12, flexShrink: 0 }}>×</button>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 9.5, color: 'var(--ss-text-3)' }}>
                        Floor
                        <input className="ss-input" type="number" step={1} value={instance.floor}
                          onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) patchInstance(instance.id, (i) => ({ ...i, floor: v as MapFloorLevel })); }}
                          style={{ width: 44, padding: '5px 6px', fontSize: 11, borderRadius: 6 }} />
                      </label>
                      {(['x', 'y', 'z'] as const).map((axis, axisIndex) => (
                        <label key={axis} style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 9.5, color: 'var(--ss-text-3)' }}>
                          {axis.toUpperCase()}
                          <input className="ss-input" type="number" step={0.5} value={instance.position[axisIndex]}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              if (!Number.isFinite(v)) return;
                              const position: [number, number, number] = [...instance.position];
                              position[axisIndex] = v;
                              patchInstance(instance.id, (i) => ({ ...i, position }));
                            }}
                            style={{ width: 44, padding: '5px 6px', fontSize: 11, borderRadius: 6 }} />
                        </label>
                      ))}
                    </div>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 9.5, color: 'var(--ss-text-3)' }}>
                      Facing (radians)
                      <input className="ss-input" type="number" step={0.1} value={instance.rotationY ?? 0}
                        onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) patchInstance(instance.id, (i) => ({ ...i, rotationY: v })); }}
                        style={{ padding: '5px 6px', fontSize: 11, borderRadius: 6 }} />
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>Patrol</span>
                      <span style={{ height: 1, flex: 1, background: 'var(--ss-border-soft)' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 9.5, color: 'var(--ss-text-3)', flex: 1 }}>
                        Loop
                        <select className="ss-input" value={instance.patrol?.loopMode ?? 'loop'}
                          onChange={(e) => patchPatrol(instance.id, (p) => ({ ...p, loopMode: e.target.value as PatrolLoopMode }))}
                          style={{ appearance: 'none', padding: '5px 6px', fontSize: 11, borderRadius: 6 }}>
                          <option value="loop">Loop</option>
                          <option value="ping-pong">Ping-pong</option>
                          <option value="once">Once</option>
                        </select>
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 9.5, color: 'var(--ss-text-3)', width: 56 }}>
                        Speed
                        <input className="ss-input" type="number" min={0.1} step={0.5} value={instance.patrol?.speed ?? 3}
                          onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && v > 0) patchPatrol(instance.id, (p) => ({ ...p, speed: v })); }}
                          style={{ padding: '5px 6px', fontSize: 11, borderRadius: 6 }} />
                      </label>
                    </div>
                    {(instance.patrol?.points ?? []).map((pt, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ fontSize: 9, color: 'var(--ss-text-3)', width: 10 }}>{i + 1}</span>
                        {(['x', 'y', 'z'] as const).map((axis) => (
                          <input key={axis} className="ss-input" type="number" step={0.5} title={axis.toUpperCase()}
                            value={axis === 'z' ? (pt.z ?? 0) : pt[axis]}
                            onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) updateWaypoint(instance.id, i, { [axis]: v }); }}
                            style={{ width: 34, padding: '4px 5px', fontSize: 10, borderRadius: 5 }} />
                        ))}
                        <input className="ss-input" type="number" title="Dwell (ms)" min={0} step={100} value={pt.dwellMs ?? 0}
                          onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && v >= 0) updateWaypoint(instance.id, i, { dwellMs: v }); }}
                          style={{ width: 46, padding: '4px 5px', fontSize: 10, borderRadius: 5 }} />
                        <button onClick={() => removeWaypoint(instance.id, i)} title="Remove waypoint" style={{ border: 'none', background: 'transparent', color: 'var(--ss-text-3)', fontSize: 12, flexShrink: 0 }}>×</button>
                      </div>
                    ))}
                    <button onClick={() => addWaypoint(instance.id, instance)} style={{ padding: '5px 8px', fontSize: 10.5, borderRadius: 6, border: '1px dashed var(--ss-border)', background: 'transparent', color: 'var(--ss-text-2)' }}>
                      + Add waypoint
                    </button>
                  </div>
                ))}
                <div style={{ fontSize: 10.5, color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
                  With no waypoints, a placement just stands at its position. The character walks between waypoints in a straight line, dwelling at each for its own delay.
                </div>
              </div>
            )}
          </>
        )}
        <M2Label>Tuning</M2Label>
        <div style={{ margin: '0 16px', padding: '12px 14px', border: '1px solid var(--ss-border-soft)', borderRadius: 10, fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
          Per-character tuning (movement, vitals, loadout) arrives with the character editor. Today every character plays with the engine defaults.
        </div>
      </M2Panel>
    </div>
  );
}
